/**
 * Logix Cache Warmer
 * On login/reconnect: fetches bootstrap data via API, populates IndexedDB cache.
 * Pre-caches HTML pages via SW cache-urls message.
 */

import { cacheSet, metaGet, metaSet } from "./db"

const BOOTSTRAP_URL = "/api/v1/sync/bootstrap"

/**
 * Warm the offline cache with fresh data from the server.
 * Called on login and after successful sync.
 */
export async function warmCache() {
  if (!navigator.onLine) return

  try {
    await acquireJwt()
    const data = await fetchBootstrap()
    if (!data) return

    // Store user profile
    await cacheSet("user", data.user)

    // Store assigned plants
    await cacheSet("plants", data.plants)

    // Store active shift data
    if (data.active_shift) {
      await cacheSet("active_shift", data.active_shift)
    }

    // Store today's checklist entries
    if (data.checklist_entries) {
      await cacheSet("checklist_entries", data.checklist_entries)
    }

    // Store org branding
    if (data.branding) {
      await cacheSet("branding", data.branding)
    }

    // Store server time for drift calculation
    await cacheSet("last_bootstrap", {
      server_time: data.server_time,
      client_time: new Date().toISOString()
    })

    // Pre-cache operator pages via service worker
    preCachePages()

    return data
  } catch (error) {
    console.warn("[CacheWarmer] Failed:", error.message)
    return null
  }
}

async function acquireJwt() {
  const existing = await metaGet("jwt_token")
  if (existing) return

  const csrfMeta = document.querySelector('meta[name="csrf-token"]')
  if (!csrfMeta) return

  try {
    const response = await fetch("/operator/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfMeta.getAttribute("content")
      },
      credentials: "same-origin"
    })

    if (!response.ok) return
    const json = await response.json()
    if (json.data?.token) {
      await metaSet("jwt_token", json.data.token)
    }
  } catch {
    // Silent fail — JWT acquisition is best-effort
  }
}

async function fetchBootstrap() {
  const token = await metaGet("jwt_token")
  const headers = { "Accept": "application/json" }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  } else {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    if (csrfMeta) headers["X-CSRF-Token"] = csrfMeta.getAttribute("content")
  }

  const response = await fetch(BOOTSTRAP_URL, {
    method: "GET",
    headers,
    credentials: "same-origin"
  })

  if (!response.ok) return null

  const json = await response.json()
  return json.data || json
}

function preCachePages() {
  if (!navigator.serviceWorker?.controller) return

  const urls = [
    "/operator/dashboard",
    "/operator/issues",
    "/offline"
  ]

  navigator.serviceWorker.controller.postMessage({
    type: "cache-urls",
    urls
  })
}
